import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

/**
 * Logo Component
 *
 * Renders a responsive logo image.
 * - By default, the logo is wrapped in a <Link> that redirects to "/unit".
 * - The clickable behavior can be disabled by setting clickable to false.
 *
 * Props:
 * - URL (string, required): The source URL of the logo image.
 * - name (string, required): The text used for the alt attribute of the image.
 * - variant (string, optional): The size variant of the logo. Accepts "large" or "small". Defaults to "large".
 * - clickable (boolean, optional): Determines if the logo is clickable. Defaults to true.
 * - className (string, optional): Additional CSS classes to extend or override the default styles.
 */
const Logo = ({ URL, name, variant = 'large', clickable = true, className = '' }) => {
  const variants = {
    large: 'w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl',
    small: 'w-full max-w-[220px] lg:max-w-xs xl:max-w-xs',
  };

  const logoImg = (
    <img
      className={`h-auto object-contain ${variants[variant]} ${className}`}
      src={URL}
      alt={name}
    />
  );

  if (clickable) {
    return (
      <Link to="/facility-selection" aria-label="Go to main dashboard">
        {logoImg}
      </Link>
    );
  }

  return logoImg;
};

Logo.propTypes = {
  URL: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['large', 'small']),
  clickable: PropTypes.bool,
  className: PropTypes.string,
};

export default React.memo(Logo);
